import { withTranslation } from "react-i18next";

import reduxUtil from "@plannel/utils/redux-util";
import { useMenuAccess } from "@plannel/hooks/useMenuAccess";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Autocomplete, Box, Chip, Snackbar, TextField } from "@mui/material";
import { AgGridReact } from "ag-grid-react";
import FilterContainer from "@plannel/components/layout/FilterContainer";
import DefaultGridSetting from "@plannel/components/aggrid/DefaultGridSetting";
import GridUtils from "@plannel/components/aggrid/GridUtils";
import PaginationContainer from "@plannel/components/PaginationContainer";
import orderTrackingService from "@plannel/services/mp/order-tracking-service";
import { MPItemAutocomplete } from "@plannel/components/filter/ItemAutocomplete";
import { MPLocationTreeFilter } from "@plannel/components/filter/LocationTreeFilter";
import ColorUtil from "@plannel/pages/utils/color-util";
import {isEmpty, isEqual, isNil, isUndefined, replace} from "lodash";
import MpVersionFilter from "@plannel/components/filter/MpVersionFilter";
import CodeUtil from "@plannel/pages/utils/code-util";
import debug from "@plannel/utils/debug-util";
import SplitterContainer from "@plannel/components/SplitterContainer";
import { useDispatch } from "react-redux";
import PeriodFilter from "@plannel/components/filter/PeriodFilter";
import TextFieldSearch from "@plannel/components/TextFieldSearch";
import {isNull} from "lodash/lang";
import NumberUtil from "@plannel/utils/number-util";

const CURRENT_DATE = new ZDate();
const INITIAL_START_DATE = new ZDate(CURRENT_DATE.getFullYear(), CURRENT_DATE.getMonth(), 1);
const INITIAL_END_DATE = new ZDate(CURRENT_DATE.getFullYear(), CURRENT_DATE.getMonth() + 12, 0);
const INITIAL_PERIOD = { start: INITIAL_START_DATE, end: INITIAL_END_DATE };

const  TRACKING_GRID = "TRACKING_GRID"
const  TRACKING_DETAIL_GRID = "TRACKING_DETAIL_GRID";
/* 자원 코드 드릴다운 대상 화면(자원 부하 분석)의 reduxKey — TabMenuList.js 의 lv3 reduxKey 와 동일해야 한다. */
const RESOURCE_UTILIZATION = "RESOURCE_UTILIZATION";
const { statusColor } = ColorUtil;
const { versionStatusCode, orderStatusCode } = CodeUtil;
const { PLANNED, RELEASED } = versionStatusCode;
const STATUS_FILTER_DATA_OPTIONS = [
  orderStatusCode.ON_TIME, orderStatusCode.DELAY, orderStatusCode.SHORT
];
const OrderTracking = ({ t, viewName, title }) => {
  const reduxViewState = reduxUtil.getViewState(viewName);
  const reduxDispatch = useDispatch();
  const { isPermitted } = useMenuAccess();

  const [loading, setLoading] = useState(false);
  const [reload, setReload] = useState(false);
  const pageSizes = [30, 50, 100];
  const [currentPage, setCurrentPage] = useState(reduxViewState?.currentPage ?? 1);
  const [pageSize, setPageSize] = useState(reduxViewState?.pageSize ?? pageSizes[0]);
  const [gridPages, setGridPages] = useState(1);
  const [sortParams, setSortParams] = useState({});
  const [snackInfo, setSnackInfo] = useState({ open: false, severity: "", content: "" });

  const [mpVersionFilter, setMpVersionFilter] = useState(reduxViewState?.mpVersion ?? null);
  const [filterValue, setFilterValue] = useState(reduxViewState?.filter ?? { locations: null });
  const [periodFilter, setPeriodFilter] = useState(
    reduxViewState?.period ?
      { start: new ZDate(reduxViewState.period.start), end: new ZDate(reduxViewState.period.end) }
      : INITIAL_PERIOD
  );
  const orderCd = useRef(reduxViewState?.orderCd ?? "");
  const [mpOrderStatusFilter, setMpOrderStatusFilter] = useState(reduxViewState?.mpOrderStatus ?? []);
  const itemSearch = useRef(reduxViewState?.itemSearch ?? "");
  const extraItemCodesRef = useRef(reduxViewState?.extraItemCodes ?? []);

  const [mpOrderData, setMpOrderData] = useState([]);
  const [orderTrackingData, setOrderTrackingData] = useState([]);
  const selectedOrderIdRef = useRef(reduxViewState?.selectedOrderId ?? null);

  const trackingGridRef = useRef();
  const trackingDetailGridRef = useRef();
  const userData = JSON.parse(localStorage.getItem("user"));
  /* Tree Data 구성을 위한 DataPath [dataPath is Array] */
  const getDataPath = useCallback((data) => {
    return data.treeDataPathCode;
  }, []);

  const gridCellStyle = (props) => {
    const { value } = props;

    let cellStyle = {};
    if (value === orderStatusCode.ON_TIME) {
      cellStyle = { backgroundColor: statusColor.SUCCESS, fontWeight: 500 };
    } else if (value === orderStatusCode.DELAY) {
      cellStyle = { backgroundColor: statusColor.STOP, fontWeight: 500 };
    } else if (value === orderStatusCode.SHORT) {
      cellStyle = { backgroundColor: statusColor.FAIL, fontWeight: 500 };
    }

    return cellStyle;
  }

  const statusBar = useMemo(() => {
    return {
      /* 상단 그리드 footer config */
      [TRACKING_GRID]: {
        statusPanels: [
          { statusPanel: "agTotalRowCountComponent", align: "center" },
          { statusPanel: "agAggregationComponent",
            statusPanelParams: {
              aggFuncs: ["sum", "count"],
            }
          }
        ]
      },
      /* 하단 그리드 footer config */
      [TRACKING_DETAIL_GRID]: {
        statusPanels: [
          { statusPanel: "agTotalRowCountComponent", align: "center" },
          { statusPanel: "agAggregationComponent",
            statusPanelParams: {
              aggFuncs: ["sum", "count", "min", "max"],
            }
          }
        ]
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const currencyRenderer = (param, isExport = false) => {
    let symbol = userData?.currencySymbol;
    const currencyCd = param?.data?.currencyCd
    if (currencyCd) {
      symbol = CodeUtil.getCurrencySymbol(currencyCd);
    }
    const value = param?.data?.[param?.colDef?.field];
    if (isNull(value) || isUndefined(value)) {
      return "";
    }
    const formatValue = NumberUtil.formatNumber(value, { maximumFractionDigits: 2, minimumFractionDigits: 2 })
    if (isExport) {
      return formatValue.replace(/,/g, "");
    }
    if (value < 0) {
      return `-${symbol}${replace(formatValue, "-", "")}`;
    }
    return symbol + formatValue;
  };

  /*
   * cellRenderer 는 최초 columnDefs 와 함께 그리드에 고정되므로 그 클로저가 잡은 state 는 낡는다
   * (버전을 바꿔도 최초 렌더 시점 값을 본다). 드릴다운에 실을 버전은 ref 로 최신값을 읽는다.
   */
  const mpVersionRef = useRef(mpVersionFilter);
  useEffect(() => {
    mpVersionRef.current = mpVersionFilter;
  }, [mpVersionFilter]);

  /*
   * 하단(추적 상세) 그리드의 자원 코드 → 자원 부하 분석(RESOURCE_UTILIZATION) 드릴다운.
   * 검색조건을 심고(updateViewState) 대상 탭을 연다(updateHistoryState → App.js 워처가 처리).
   *
   * [주의] updateViewState 는 같은 viewName 의 기존 상태에 "병합"된다(viewStates.js). 이전 방문에서
   * 남은 조건이 살아 결과를 과도하게 거를 수 있으므로 removeViewState 로 먼저 비우고, 대상 화면이
   * reduxViewState 에서 읽는 필드를 정확한 이름·형태로 모두 지정한다(floNavActions.js 헤더 참조).
   *   - resource : 객체 {resCd, name} — ResourceUtilization 은 resourceSearch 가 아니라 resource 사용.
   *                자원명은 상세 그리드에 없으므로 코드를 표시명으로 쓴다(floNavActions 의 resObj 와 동일).
   *                동시자원을 쓴 공정은 셀에 자원들이 콤마로 묶여 표시되므로("A,B") 그 문자열을 그대로
   *                넘기면 조회되지 않는다 → 항상 맨 앞에 오는 메인자원 하나만 잘라서 넘긴다.
   *   - version  : 이 화면에서 보던 MP 버전을 그대로 넘겨 같은 계획 기준으로 조회되게 한다.
   *                (같은 planVersionService/파서로 받은 완전한 객체라 부분 주입 이슈가 없다.
   *                 없으면 null → 대상 화면이 자기 기본(최신) 버전을 고른다.)
   */
  const openResourceUtilization = (resCd) => {
    /* 동시자원("A,B" / "A, B") → 메인자원(맨 앞) 하나만 사용 */
    const code = isNil(resCd) ? "" : String(resCd).split(",")[0].trim();
    if (isEmpty(code)) {
      return;
    }

    const payload = {
      currentPage: 1,
      resource: { resCd: code, name: code },
      version: !isEmpty(mpVersionRef.current) ? mpVersionRef.current : null,
      filter: { locations: null }
    };

    reduxDispatch(reduxUtil.removeViewState({ viewName: RESOURCE_UTILIZATION }));
    reduxDispatch(reduxUtil.updateViewState(reduxUtil.createObjectState(RESOURCE_UTILIZATION, payload)));
    reduxDispatch(reduxUtil.updateHistoryState(RESOURCE_UTILIZATION));
  }

  /* 자원 코드 링크 셀 — 값이 없는 행(품목/라우트 레벨)은 링크로 만들지 않는다. */
  const resourceLinkRenderer = (params) => {
    const { value } = params;
    if (isNil(value) || value === "") {
      return null;
    }
    // 자원 부하 분석이 권한 밖이면 링크가 아니라 평문으로 — 밑줄·포인터까지 함께 뺀다.
    if (!isPermitted(RESOURCE_UTILIZATION)) return value;

    return (
      <div style={{ color: "#1976d2", textDecoration: "underline", cursor: "pointer" }}
        onClick={() => openResourceUtilization(value)}>{value}</div>
    );
  }

  const orderTrackingColumnDefs = [
    { headerName: "planSeq", field: "planSeqNum", width: 100, type: ["nonEditableColumn"] },
    { headerName: "orderCd", field: "orderCd", width: 200, type: ["nonEditableColumn"], cellClass: 'stringType' },
    { headerName: "locName", field: "locName", width: 150, type: ["nonEditableColumn"] },
    { headerName: "itemCd", field: "itemCd", width: 150, type: ["nonEditableColumn"] },
    { headerName: "itemName", field: "itemName", width: 150, type: ["nonEditableColumn"] },
    { headerName: "dueDate", field: "dueDate", width: 140, type: ["nonEditableColumn"] },
    { headerName: "orderQty", field: "orderQty", width: 120, type: ["nonEditableColumn", "numberColumn", "rightAligned"] },
    { headerName: "supplyQty", field: "shipQty", width: 120, type: ["nonEditableColumn", "numberColumn", "rightAligned"] },
    { headerName: "supplyDate", field: "shipDate", width: 120, type: ["nonEditableColumn"] },
    {
      headerName: "orderStatus", field: "orderStatus", width: 120, type: ["nonEditableColumn"],
      cellStyle: gridCellStyle,
      valueFormatter: (params) => {
        return GridUtils.gridValueL10N(params.value, { headerName: "mpOrderStatus" })
      }
    },
    { headerName: "reqLocName", field: "reqLocName", width: 150, type: ["nonEditableColumn"] },
    { headerName: "customerName", field: "customerName", width: 150, type: ["nonEditableColumn"] },
    { headerName: "orderAmt", field: "orderAmt", width: 150, type: ["nonEditableColumn", "currencyNumberColumn", "rightAligned"], valueFormatter: currencyRenderer },
    { headerName: "problemCause", field: "problemCause", width: 150, type: ["nonEditableColumn"] },
    { headerName: "problemDesc", field: "problemDesc", minWidth: 200, type: ["nonEditableColumn"], flex: 1 },
  ];

  const orderTrackingDetailColumnDefs = [
    // { headerName: "orderCd", field: "orderCd", width: 150, type: ["nonEditableColumn"] }, /* 전체 Order Code 확인용  */
    // { headerName: "bomLevel", field: "bomLevel", width: 130, type: ["nonEditableColumn"] },
    { headerName: "activityType", field: "activityType", width: 110, type: ["nonEditableColumn"], sortable: false, valueFormatter: (params) => (isNil(params.value) ? "" : GridUtils.gridValueL10N(params.value)) },
    { headerName: "itemCd", field: "itemCd", width: 130, type: ["nonEditableColumn"] },
    { headerName: "productionRoute", field: "routeName", width: 120, type: ["nonEditableColumn"], sortable: false },
    /* 자원 코드 클릭 → 자원 부하 분석 화면으로 이동(그 자원으로 필터) */
    { headerName: "resource", field: "resCd", width: 120, type: ["nonEditableColumn"], sortable: false, cellClass: 'stringType', cellRenderer: resourceLinkRenderer },
    // { headerName: "detail", field: "detail", width: 150, type: ["nonEditableColumn"] },
    { headerName: "reqQty", field: "reqQty", width: 120, type: ["nonEditableColumn", "numberColumn", "rightAligned"], sortable: false },
    { headerName: "invUsageQty", field: "invUsageQty", width: 120, type: ["nonEditableColumn", "numberColumn", "rightAligned"], sortable: false },
    { headerName: "productionQty", field: "prodQty", width: 120, type: ["nonEditableColumn", "numberColumn", "rightAligned"], sortable: false },
    { headerName: "supplyQty", field: "shipQty", width: 120, type: ["nonEditableColumn", "numberColumn", "rightAligned"], sortable: false },
    { headerName: "prodDate", field: "prodDate", width: 140, type: ["nonEditableColumn"] },
    { headerName: "supplyDate", field: "shipDate", minWidth: 140, type: ["nonEditableColumn"], sortable: false},

  ];

  const autoGroupColumnDef = useMemo(() => {
    /* using tree data */
    return {
      headerName: t("bom"),
      // field: "bomLevel",
      cellRendererParams: {
        suppressCount: true,
      },
      minWidth: 280,
      valueGetter: (params) => {
        const { data } = params;
        if (isEmpty(data)) {
          const { node } = params;
          return node.key;
        }

        const { itemName, resCd } = data;
        if (isEmpty(resCd)) {
          return itemName;
        }

        return `${itemName}`;
      },
      valueFormatter: (params) => {
        const { value } = params;
        if (isNil(value) || value === "") {
          return "";
        }

        const splitValue = String(value).split("#");
        if (splitValue.length > 3) {
          return `${splitValue[0]}#${splitValue[2]}`;
        }

        return value;
      },
      type: ["nonEditableColumn"]
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t]);

  const getRequestParams = (isPagination = true) => {
    const params = {};

    if(mpVersionFilter && !isEmpty(mpVersionFilter)) {
      params.versionId = mpVersionFilter.id;
    }

    if (filterValue?.locations) {
      params.locCd = filterValue?.locations?.map(location => location?.locCd);
    }

    if (extraItemCodesRef.current?.length > 0) {
      params.items = [itemSearch.current, ...extraItemCodesRef.current].filter(Boolean);
    } else if (itemSearch.current) {
      params.item = itemSearch.current;
    }

    if (!isEmpty(mpOrderStatusFilter)) {
      params.orderStatus = mpOrderStatusFilter;
    }

    if (periodFilter?.start) {
      params.startDate = periodFilter.start;
    }

    if (periodFilter?.end) {
      params.endDate = periodFilter.end;
    }

    if (orderCd.current) {
      params.orderCd = orderCd.current;
    }

    if (isPagination) {
      if (currentPage) {
        params.page = currentPage - 1;
      }

      if (pageSize) {
        params.pageSize = pageSize;
      }
    }

    return { ...params, ...sortParams };
  }

  const getSortedData = gridData => {
    return gridData.sort((a, b) => {
      if (a?.planSeqNum === null && b?.planSeqNum === null) {
        return 0;
      }
      if (a?.planSeqNum === null) {
        return 1;
      }
      if (b?.planSeqNum === null) {
        return -1;
      }

      if (a?.planSeqNum === -1) {
        return 1;
      }

      if (a?.planSeqNum < b.planSeqNum) {
          return -1;
      }

      if (a.planSeqNum > b.planSeqNum) {
          return 1;
      }
      return 0;
    });
  }

  const applyLangPackToProblemDesc = (results, t) => {
    if (!Array.isArray(results)) return results;
    return results.map(item => {
      if (typeof item.problemDesc === 'string') {
        item.problemDesc = item.problemDesc.replace(/\$\{([^}]+)\}/g, (match, key) => t(key));
      }
      return item;
    });
  };

  const retrieveOrderTracking = () => {
    trackingGridRef?.current?.api?.setGridOption('loading', true);

    const params = getRequestParams();
    setOrderTrackingData([]);

    orderTrackingService.getOrderTrackingResult(params)
      .then((response) => {
        const { results, totalPages } = response.data;

        setGridPages(totalPages ?? 1);

        const localizedResults = applyLangPackToProblemDesc(results, t);
        const sortedGridData = getSortedData(localizedResults);
        setMpOrderData(sortedGridData);

        /* 조회 결과(0건 포함)에 선택 row가 없으면 선택 해제 */
        if (!isNil(selectedOrderIdRef.current)
          && !sortedGridData.some(row => String(row?.id) === String(selectedOrderIdRef.current))) {
          selectedOrderIdRef.current = null;
        }
      })
      .catch((error) => {
        debug(error);
      })
      .then(() => {
        const reduxState = reduxUtil.createObjectState(viewName, { itemSearch: itemSearch.current, extraItemCodes: extraItemCodesRef.current, mpOrderStatus: mpOrderStatusFilter, orderCd: orderCd.current, currentPage, pageSize, selectedOrderId: selectedOrderIdRef.current });
        reduxDispatch(reduxUtil.updateViewState(reduxState));
      })
      .finally(() => {
        trackingGridRef.current?.api?.setGridOption('loading', false);
        setReload(false);
      })
  }

  const initPageToRetrieve = () => {
    if (currentPage > 1) {
      setCurrentPage(1);
    } else {
      retrieveOrderTracking();
    }
  }

  const onRowClickedHandler = (params) => {
    const { data } = params;
    const orderTrackingResult = data?.mpTrackingDtoResults ?? [];

    selectedOrderIdRef.current = data?.id ?? null;
    reduxDispatch(reduxUtil.updateViewState(reduxUtil.createObjectState(viewName, { selectedOrderId: selectedOrderIdRef.current })));

    setOrderTrackingData(orderTrackingResult);
  }

  /* 상단 그리드 데이터 갱신 후 redux에 저장된 선택 row 복원 -> 하단 그리드 재구성 */
  const restoreSelectedRow = (params) => {
    const selectedOrderId = selectedOrderIdRef.current;

    let selectedNode = null;
    params.api.forEachNode(node => {
      if (!isNil(selectedOrderId) && String(node.data?.id) === String(selectedOrderId)) {
        selectedNode = node;
      }
    });

    /* 복원할 선택 row 가 없으면 첫 행 선택 */
    selectedNode = selectedNode ?? params.api.getDisplayedRowAtIndex(0);

    if (selectedNode) {
      selectedNode.setSelected(true);
      params.api.ensureNodeVisible(selectedNode);
      onRowClickedHandler({ data: selectedNode.data });
    }
  }

  const onGridReady = (params, gridId) => {
    if (gridId === TRACKING_GRID) {
      GridUtils.setColumnDefs({ ...params, columnDefs: orderTrackingColumnDefs, viewName, gridId, initState: true });
    } else {
      GridUtils.setColumnDefs({ ...params, columnDefs: orderTrackingDetailColumnDefs, viewName, gridId, initState: true });
    }
  }

  const orderStatusOnChangeHandler = (_, newValue) => {
    setMpOrderStatusFilter(newValue);
  }

  const getChipColor = (statusCode) => {
    switch(statusCode) {
      case orderStatusCode.ON_TIME:
        return statusColor.SUCCESS;
      case orderStatusCode.DELAY:
        return statusColor.STOP;
      case orderStatusCode.SHORT:
        return statusColor.FAIL;
      default:
        return statusColor.IDLE;
    }
  }

  const defaultOrderTrackingGridMemo = useMemo(() => {
    return DefaultGridSetting({ title, viewName, columnDefs: orderTrackingColumnDefs, gridId: TRACKING_GRID });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const defaultOrderTrackingDetailGridMemo = useMemo(() => {
    return DefaultGridSetting({ title, viewName, columnDefs: orderTrackingDetailColumnDefs, gridId: TRACKING_DETAIL_GRID });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setLoading(true);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (loading && !isEmpty(mpVersionFilter)) {
      if (!isEqual(reduxViewState?.mpOrderStatus, mpOrderStatusFilter)
        || !isEqual(reduxViewState?.filter, filterValue)
        || !isEqual(reduxViewState?.period ? { start: new ZDate(reduxViewState.period.start), end: new ZDate(reduxViewState.period.end) } : {} , periodFilter)) {
        initPageToRetrieve();
      } else {
        setReload(true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mpOrderStatusFilter, filterValue, periodFilter]);

  useEffect(() => {
    if (!isEmpty(mpVersionFilter)) {
      setPeriodFilter({ start: new ZDate(mpVersionFilter?.planConfigObj?.PLAN_START_DATE), end: new ZDate(mpVersionFilter?.planConfigObj?.PLAN_END_DATE) });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mpVersionFilter]);

  useEffect(() => {
    if (loading && !isEmpty(mpVersionFilter)) {
      setReload(true);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, sortParams, pageSize]);

  useEffect(() => {
    if (loading && !isEmpty(mpVersionFilter) && reload) {
      retrieveOrderTracking();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reload]);

  return (
    <>
      <FilterContainer hasIcons={false}>
        <MpVersionFilter viewName={viewName} mpVersionValue={mpVersionFilter} onChangeFilter={setMpVersionFilter} statusFilters={[PLANNED, RELEASED]} />
        <MPLocationTreeFilter viewName={viewName} filterValue={filterValue?.locations?.map(location => location?.locCd)} onChangeFilter={setFilterValue} />
        <PeriodFilter viewName={viewName} period={periodFilter} setPeriod={setPeriodFilter} dateType={"day"} />
        <MPItemAutocomplete retrieve={initPageToRetrieve} searchValue={itemSearch} extraCodesRef={extraItemCodesRef}/>
        <TextFieldSearch sx={{ width: 200, mr: 1 }} label={"orderCd"} searchCode={orderCd} retrieve={retrieveOrderTracking}/>
        <Autocomplete
          key={"order-status-auto-complete"}
          options={STATUS_FILTER_DATA_OPTIONS}
          getOptionLabel={option => {
            return GridUtils.gridValueL10N(option, { headerName: "mpOrderStatus" });
          }}
          value={mpOrderStatusFilter}
          onChange={orderStatusOnChangeHandler}
          isOptionEqualToValue={(option, value) => option === value}
          size="small"
          sx={{ mr: 1, minWidth: 240, maxWidth: 360}}
          multiple
          filterSelectedOptions
          autoCapitalize="none"
          renderTags={(tagValue, getTagProps) =>
            tagValue.map((option, index) => {
                return(
                  <Chip
                    key={option}
                    label={GridUtils.gridValueL10N(option, { headerName: "mpOrderStatus" })}
                    {...getTagProps({index})}
                    size="small"
                    style={{ backgroundColor: getChipColor(option), fontWeight: 500 }}
                  />
                )}
              )
            }
          renderInput={(params) => {
            return (<TextField {...params} label={t("orderStatus")} />)
          }}
        />
      </FilterContainer>
      <Box className="grid-area" sx={{ display: "flex", flexDirection: "column"}}>
        <SplitterContainer initialSizes={[1.3, 1]} collapseDirection='up'>
          <Box height="100%">
            <Box className={"ag-theme-balham"} height="calc(100% - 50px)" sx={{ mb: 1 }}>
              <AgGridReact
                ref={trackingGridRef}
                rowData={mpOrderData}
                {...defaultOrderTrackingGridMemo}
                onRowClicked={onRowClickedHandler}
                onRowDataUpdated={restoreSelectedRow}
                onGridReady={(params) => onGridReady(params, TRACKING_GRID)}
                onSortChanged={params => setSortParams(GridUtils.getSortState(params))}
                rowSelection={"single"}
                statusBar={statusBar[TRACKING_GRID]}
              />
            </Box>
            <PaginationContainer
              currentPage={currentPage} totalPages={gridPages}
              pageSize={pageSize} pageSizes={pageSizes}
              setCurrentPage={setCurrentPage} setPageSize={setPageSize}
            />
          </Box>
          <Box className={"ag-theme-balham"} height="calc(100% - 20px)" sx={{ mt: 2 }}>
            <AgGridReact
              ref={trackingDetailGridRef}
              rowData={orderTrackingData}
              {...defaultOrderTrackingDetailGridMemo}
              onGridReady={(params) => onGridReady(params, TRACKING_DETAIL_GRID)}
              onSortChanged={params => setSortParams(GridUtils.getSortState(params))}
              treeData
              getDataPath={getDataPath}
              statusBar={statusBar[TRACKING_DETAIL_GRID]}
              groupDefaultExpanded={-1}
              autoGroupColumnDef={autoGroupColumnDef}
            />
          </Box>
        </SplitterContainer>
      </Box>
      <Snackbar
        open={snackInfo.open}
        onClose={() => setSnackInfo({ ...snackInfo, open: false })}
        severity={snackInfo.severity}
        content={snackInfo.content}
      />
    </>
  )
}

export default withTranslation()(OrderTracking);
